/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { memo, useCallback, useEffect, useRef } from 'react';

import { COMMAND_INTELLIGENCE_COMPOSER_MAX_HEIGHT_PX } from './constants';
import { getCommandIntelligencePromptInputStyles } from './CommandIntelligencePromptInput.styles';
import Send from '@mui/icons-material/Send';
import Stop from '@mui/icons-material/Stop';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const CommandIntelligencePromptInput = ({
  value,
  onChange,
  onSubmit,
  onStop,
  placeholder,
  disabled = false,
  isLoading = false,
  canStop,
  leftActions = null,
  showKeyboardHint = true,
  compact = false,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const styles = getCommandIntelligencePromptInputStyles(theme, { compact });
  const textareaRef = useRef(null);

  const inputAriaLabel = intl.formatMessage({ id: 'command_intelligence_composer_input_aria' });
  const sendAriaLabel = intl.formatMessage({ id: 'command_intelligence_send_aria' });
  const stopAriaLabel = intl.formatMessage({ id: 'command_intelligence_stop_aria' });
  const isStopMode = Boolean(isLoading && (canStop ?? Boolean(onStop)));
  const hintMessageId = isStopMode
    ? 'command_intelligence_composer_stop_hint'
    : 'command_intelligence_composer_submit_hint';

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, COMMAND_INTELLIGENCE_COMPOSER_MAX_HEIGHT_PX)}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  const handleRootClick = useCallback(event => {
    if (event.target.closest('button')) {
      return;
    }

    textareaRef.current?.focus();
  }, []);

  const handleChange = useCallback(
    event => {
      onChange?.(event.target.value);
    },
    [onChange]
  );

  const handleSubmit = useCallback(() => {
    const trimmed = value?.trim?.() ?? '';

    if (!trimmed || disabled || isLoading) {
      return;
    }

    onSubmit?.(trimmed);
  }, [disabled, isLoading, onSubmit, value]);

  const handleStop = useCallback(() => {
    if (!isStopMode) {
      return;
    }

    onStop?.();
  }, [isStopMode, onStop]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape' && isStopMode) {
        event.preventDefault();
        handleStop();

        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleStop, handleSubmit, isStopMode]
  );

  const canSend = Boolean(value?.trim()) && !disabled && !isLoading;
  const primaryActionLabel = isStopMode ? stopAriaLabel : sendAriaLabel;

  return (
    <Box
      sx={[styles.root, compact ? styles.rootCompact : null, isStopMode ? styles.rootLoading : null]}
      onClick={handleRootClick}
    >
      <Box
        component="textarea"
        ref={textareaRef}
        rows={1}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={inputAriaLabel}
        aria-busy={isStopMode}
        sx={styles.textarea}
      />

      <Box sx={styles.actions}>
        {!compact ? (
          <Box sx={styles.actionGroup}>
            {leftActions}
            {showKeyboardHint && !disabled ? (
              <Box component="span" sx={styles.hint}>
                {intl.formatMessage({ id: hintMessageId })}
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Box sx={styles.actionGroup}>
          <Tooltip title={primaryActionLabel}>
            <span>
              <IconButton
                type="button"
                size="small"
                onClick={isStopMode ? handleStop : handleSubmit}
                disabled={isStopMode ? !onStop : !canSend}
                aria-label={primaryActionLabel}
                sx={isStopMode ? styles.stopButton : styles.sendButton}
              >
                {isStopMode ? (
                  <Stop sx={styles.actionIcon} />
                ) : (
                  <Send sx={[styles.actionIcon, styles.actionIconOnCta]} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

CommandIntelligencePromptInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onStop: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  canStop: PropTypes.bool,
  leftActions: PropTypes.node,
  showKeyboardHint: PropTypes.bool,
  compact: PropTypes.bool,
};

export default memo(CommandIntelligencePromptInput);
